/** @jsxImportSource preact */
import { h } from 'preact';
import { useState, useRef, useEffect, useMemo } from 'preact/hooks';
import type { StackByDim } from './IncidentChart';
import { IMPACT_LABELS, NATSEC_FRAMEWORK_URL, type ImpactPillVariant } from '../data/impactScale';
import { HARM_SCALE_URL } from '../data/harmSeverityScale';
import { TEXT_LINK_COLOR } from '../lib/theme';

// ============================================
// Types
// ============================================

// FilterDim/StackBy key is intentionally a free-form string so this filter bar
// can be reused by non-incident chart families (e.g. Governance Mapping) whose
// dimension keys don't fit the incident vocabulary.
export type FilterDimKey = string;

// Stack-by value passed in/out of the selector. `null` represents "no
// stacking" (controlled by `allowNoneStackBy`). Each chart family owns its
// own narrower string-union (e.g. StackByDim for incidents, StackByOption
// for governance) and passes those values through this generic shape.
export type StackByValue = string;

export interface FilterDim {
  key: FilterDimKey;
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** 'multi' (default): checkbox multi-select. 'min': single-select with a
   * "≥" semantic — chip reads `Label ≥ value`. */
  kind?: 'multi' | 'min';
  /** Optional hover tooltip — used as the `title` attribute on the chip
   * and on the dimension's entry in the Add-filter menu. */
  tooltip?: string;
}

export interface SeverityField {
  key: string;
  label: string;
  /**
   * Override for the URL/filter param name. Default is computed from `key`
   * via the convention `minSeverity${Capitalised(key minus 'SeverityDirect')}`.
   * NatSec fields supply this directly (e.g. paramKey: 'minNatSecPhysical')
   * so they don't get force-mangled into a `minSeverity*` shape.
   */
  paramKey?: string;
}

export interface IncidentFilterBarProps<S extends string = string> {
  // Stack-by (or "View by" for non-stacked charts like pie)
  availableStackBy: S[];
  stackBy: S | null;
  onStackByChange: (next: S | null) => void;
  stackByLabels: Record<string, string>;
  /** Optional per-dimension tooltips shown on hover in the Stack-by menu. */
  stackByTooltips?: Record<string, string>;
  /** Label rendered before the selector. Defaults to "Stack by". Use "View by"
   * for pie/donut where the selector picks the slicing dimension. */
  stackByControlLabel?: string;
  /** Whether to include a "None" option in the selector. Default true (bars).
   * Set false for pies, which always need a slicing dimension. */
  allowNoneStackBy?: boolean;

  // Dimensions (year + categorical filters)
  dimensions: FilterDim[];

  // Severity
  severityFields: SeverityField[];
  severityFilters: Record<string, number>;
  onSeverityChange: (next: Record<string, number>) => void;

  /**
   * Optional second severity-style group. When provided, the filter bar
   * renders a second "<label>" pill that opens an identical popover
   * (overall-≥ shortcut + per-category overrides). Used by the NatSec
   * incident view to surface its impact dimensions.
   */
  natSecSeverityFields?: SeverityField[];
  natSecSeverityFilters?: Record<string, number>;
  onNatSecSeverityChange?: (next: Record<string, number>) => void;
  /** Label on the harm-severity pill/popover. Default 'Severity'. The
   * NatSec view overrides it to 'Harm Severity', where a bare 'Severity'
   * would be ambiguous alongside the NatSec Impact control. */
  severityLabel?: string;
  natSecSeverityLabel?: string;
  natSecHighestKey?: string;
  natSecHighestLabel?: string;

  // Reset
  hasActiveFilter: boolean;
  onResetAll: () => void;

  isMobile: boolean;
}

// Severity is special-cased: its popover lives next to the filter row but
// edits a separate piece of state (a record of category → min score).
// `minSeverityHighest` is the "any category" shortcut.
const SEVERITY_HIGHEST_KEY = 'minSeverityHighest';

// Severity levels are shown by name, never as 1–5: a reader new to the page
// has no way to know what "3" means. Harm severity and NatSec impact share
// the words except at 5 (Catastrophic / Critical), and each links the page
// that defines its levels.
const SCALE_LINKS: Record<ImpactPillVariant, { href: string; text: string }> = {
  standard: { href: HARM_SCALE_URL, text: 'this scale' },
  natsec: { href: NATSEC_FRAMEWORK_URL, text: 'this framework' },
};

function levelName(variant: ImpactPillVariant, score: number): string {
  return IMPACT_LABELS[variant][score] ?? String(score);
}

// Every pill in the filter bar (Stack-by select, Add filter, Severity, chips)
// shares one set of dimensions so host-page CSS (Webflow) can't make some
// taller than others. We pin height + padding + border-radius explicitly.
const PILL_HEIGHT = 24;
const PILL_RADIUS = 12;

function pillStyle(borderStyle: 'solid' | 'dashed' = 'solid', highlighted = false): h.JSX.CSSProperties {
  return {
    boxSizing: 'border-box',
    height: PILL_HEIGHT,
    padding: '0 10px',
    fontSize: 11,
    lineHeight: `${PILL_HEIGHT - 2}px`,
    border: `1px ${borderStyle} ${highlighted ? 'var(--gc-link, currentColor)' : 'var(--gc-control-border, currentColor)'}`,
    borderRadius: PILL_RADIUS,
    background: 'transparent',
    color: 'inherit',
    fontFamily: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    margin: 0,
  };
}

// ============================================
// Helpers
// ============================================

const getPageBackground = (): string => {
  if (typeof window === 'undefined') return '#ffffff';
  const bg = getComputedStyle(document.body).backgroundColor;
  if (!bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)') return '#ffffff';
  return bg;
};

function severitySummary(
  filters: Record<string, number>,
  fields: SeverityField[],
  label: string = 'Severity',
  highestKey: string = SEVERITY_HIGHEST_KEY,
  variant: ImpactPillVariant = 'standard',
): string {
  const active = Object.entries(filters).filter(([, v]) => v > 0);
  if (active.length === 0) return label;

  const highest = filters[highestKey] || 0;
  const perCat = active.filter(([k]) => k !== highestKey);

  if (highest > 0 && perCat.length === 0) return `${label}: any ≥ ${levelName(variant, highest)}`;
  if (highest === 0 && perCat.length === 1) {
    const [paramKey, val] = perCat[0];
    const field = fields.find((f) => paramKeyFor(f) === paramKey);
    return `${label}: ${field?.label ?? 'category'} ≥ ${levelName(variant, val)}`;
  }
  return `${label}: ${active.length} condition${active.length === 1 ? '' : 's'}`;
}

function paramKeyFor(sf: SeverityField): string {
  if (sf.paramKey) return sf.paramKey;
  // Mirrors the param-key derivation used in IncidentChart's existing logic.
  return `minSeverity${sf.key.replace('SeverityDirect', '').replace(/^./, (c) => c.toUpperCase())}`;
}

// ============================================
// Floating popover wrapper
// ============================================

interface PopoverProps {
  anchorRef: { current: HTMLElement | null };
  onClose: () => void;
  isMobile: boolean;
  children: any;
  minWidth?: number;
  maxWidth?: number;
}

function Popover({ anchorRef, onClose, isMobile, children, minWidth = 220, maxWidth = 380 }: PopoverProps) {
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const margin = 8;

    // Horizontal placement: prefer left-aligned with the trigger; if that
    // would overflow the right edge, shift left to fit.
    const left = Math.max(margin, Math.min(rect.left, viewportW - margin - minWidth));

    // Vertical placement: try below the trigger first. If the room there
    // is too tight to scroll comfortably and there's more space above,
    // flip the popover up so it grows from the trigger top. Either way,
    // cap maxHeight to the available room — otherwise the popover extends
    // past the viewport and its bottom items are unreachable (fixed
    // positioning ignores page scroll).
    const spaceBelow = viewportH - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const flipAbove = spaceBelow < 180 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(160, Math.min(viewportH * 0.7, flipAbove ? spaceAbove : spaceBelow));
    const top = flipAbove
      ? Math.max(margin, rect.top - maxHeight - 4)
      : rect.bottom + 4;

    setPos({ top, left, maxHeight });
  }, [anchorRef]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current && panelRef.current.contains(target)) return;
      if (anchorRef.current && anchorRef.current.contains(target)) return;
      onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [anchorRef, onClose]);

  if (!pos) return null;

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        minWidth,
        maxWidth,
        maxHeight: pos.maxHeight,
        overflowY: 'auto',
        // -webkit-overflow-scrolling for older iOS Safari; modern Safari
        // scrolls overflow:auto natively but the legacy flag is harmless.
        WebkitOverflowScrolling: 'touch',
        // Touch scrolling inside this fixed-position panel should stay
        // inside it — don't let the underlying chart canvas absorb the
        // swipe via scroll chaining.
        overscrollBehavior: 'contain',
        background: getPageBackground(),
        color: 'inherit',
        border: '1px solid var(--gc-control-border, currentColor)',
        borderRadius: 8,
        boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
        zIndex: 1000,
        padding: 8,
        fontSize: isMobile ? 13 : 12,
      }}
    >
      {children}
    </div>
  );
}

// ============================================
// Stack-by selector
// ============================================

interface StackByButtonProps {
  available: StackByValue[];
  value: StackByValue | null;
  labels: Record<string, string>;
  controlLabel: string;
  isOpen: boolean;
  onToggle: () => void;
  triggerRef: { current: HTMLButtonElement | null };
}

/**
 * Stack-by trigger. Renders a label-text span next to a button styled with
 * the shared pillStyle() — identical DOM/CSS shape to "+ Add filter" and
 * "Severity", so host pages (Webflow especially) can't single out a native
 * <select> for form-control styling and knock it out of vertical alignment.
 */
function StackByButton({ available, value, labels, controlLabel, isOpen, onToggle, triggerRef }: StackByButtonProps) {
  if (available.length === 0) return null;

  const currentLabel = value ? labels[value] : 'None';

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
      <span style={{ opacity: 0.7, whiteSpace: 'nowrap' }}>{controlLabel}</span>
      <button
        ref={triggerRef}
        type="button"
        onClick={onToggle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        style={pillStyle('solid', value !== null)}
      >
        {currentLabel}
        <span style={{ marginLeft: 6, fontSize: 8, opacity: 0.6 }}>▾</span>
      </button>
    </span>
  );
}

interface StackByMenuProps {
  available: StackByValue[];
  value: StackByValue | null;
  labels: Record<string, string>;
  tooltips?: Record<string, string>;
  allowNone: boolean;
  controlLabel: string;
  onPick: (next: StackByValue | null) => void;
}

function StackByMenu({ available, value, labels, tooltips, allowNone, controlLabel, onPick }: StackByMenuProps) {
  const rowStyle = (active: boolean): h.JSX.CSSProperties => ({
    textAlign: 'left',
    background: active ? 'rgba(127,127,127,0.12)' : 'none',
    border: 'none',
    padding: '5px 8px',
    fontSize: 12,
    color: 'inherit',
    cursor: 'pointer',
    borderRadius: 4,
    fontWeight: active ? 600 : 400,
    display: 'block',
    width: '100%',
  });
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, opacity: 0.7 }}>
        {controlLabel}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {allowNone && (
          <button
            type="button"
            onClick={() => onPick(null)}
            style={rowStyle(value === null)}
          >
            None
          </button>
        )}
        {available.map((dim) => (
          <button
            key={dim}
            type="button"
            onClick={() => onPick(dim)}
            title={tooltips?.[dim]}
            style={rowStyle(value === dim)}
          >
            {labels[dim]}
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================
// Value picker (checkbox list inside a popover)
// ============================================

interface ValuePickerProps {
  dim: FilterDim;
}

function ValuePicker({ dim }: ValuePickerProps) {
  const [search, setSearch] = useState('');
  const isMin = dim.kind === 'min';
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return dim.options;
    return dim.options.filter((o) => o.label.toLowerCase().includes(q));
  }, [dim.options, search]);

  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          marginBottom: 6,
          opacity: 0.7,
        }}
      >
        {isMin ? `${dim.label} ≥` : dim.label}
      </div>
      {dim.options.length > 8 && (
        <input
          type="text"
          value={search}
          onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
          placeholder="Search"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '4px 8px',
            fontSize: 11,
            border: '1px solid var(--gc-control-border, currentColor)',
            borderRadius: 6,
            background: 'transparent',
            color: 'inherit',
            marginBottom: 6,
          }}
        />
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: isMin ? 'flex-end' : 'space-between',
          fontSize: 10,
          paddingBottom: 4,
          marginBottom: 4,
          borderBottom: '1px solid var(--gc-control-border, #d1d5db)',
        }}
      >
        {!isMin && (
          <button
            type="button"
            onClick={() => dim.onChange(dim.options.map((o) => o.value))}
            style={{ background: 'none', border: 'none', color: 'var(--gc-link, inherit)', cursor: 'pointer', padding: 0 }}
          >
            Select all
          </button>
        )}
        <button
          type="button"
          onClick={() => dim.onChange([])}
          style={{ background: 'none', border: 'none', color: 'var(--gc-link, inherit)', cursor: 'pointer', padding: 0 }}
        >
          Clear
        </button>
      </div>
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {filtered.map((opt) => {
          const checked = isMin
            ? dim.selected[0] === opt.value
            : dim.selected.includes(opt.value);
          return (
            <label
              key={opt.value}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '3px 2px',
                fontSize: 11,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <input
                type={isMin ? 'radio' : 'checkbox'}
                name={isMin ? `picker-${dim.key}` : undefined}
                checked={checked}
                onChange={() => {
                  if (isMin) {
                    dim.onChange(checked ? [] : [opt.value]);
                  } else {
                    const next = checked
                      ? dim.selected.filter((v) => v !== opt.value)
                      : [...dim.selected, opt.value];
                    dim.onChange(next);
                  }
                }}
                style={{ accentColor: 'var(--gc-link, currentColor)' }}
              />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.label}</span>
            </label>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ fontSize: 11, opacity: 0.5, padding: '6px 2px' }}>No matches</div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Filter chip
// ============================================

interface FilterChipProps {
  dim: FilterDim;
  isOpen: boolean;
  onEdit: () => void;
  onClear: () => void;
  triggerRef: { current: HTMLButtonElement | null };
}

function FilterChip({ dim, isOpen, onEdit, onClear, triggerRef }: FilterChipProps) {
  const display = useMemo(() => {
    if (dim.selected.length === 0) return dim.label;
    if (dim.kind === 'min') {
      // 'min' is single-select with a ≥ semantic; first value is the only one.
      const opt = dim.options.find((o) => o.value === dim.selected[0]);
      return `${dim.label} ≥ ${opt?.label ?? dim.selected[0]}`;
    }
    if (dim.selected.length === 1) {
      const opt = dim.options.find((o) => o.value === dim.selected[0]);
      return `${dim.label}: ${opt?.label ?? dim.selected[0]}`;
    }
    // Show first value + overflow count when more are selected
    const first = dim.options.find((o) => o.value === dim.selected[0])?.label ?? dim.selected[0];
    return `${dim.label}: ${first} +${dim.selected.length - 1}`;
  }, [dim]);

  // Container handles the border + height; inner buttons strip their native
  // chrome so host-page CSS can't add extra padding/min-height that would
  // make the chip taller than the other pills.
  const innerBtn: h.JSX.CSSProperties = {
    boxSizing: 'border-box',
    height: '100%',
    background: 'none',
    border: 'none',
    color: 'inherit',
    fontFamily: 'inherit',
    fontSize: 11,
    lineHeight: 1,
    cursor: 'pointer',
    margin: 0,
    display: 'inline-flex',
    alignItems: 'center',
  };
  return (
    <span
      style={{
        ...pillStyle('solid', true),
        padding: 0,
        background: isOpen ? 'rgba(127,127,127,0.08)' : 'transparent',
        overflow: 'hidden',
      }}
      title={dim.tooltip}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={onEdit}
        style={{
          ...innerBtn,
          padding: '0 4px 0 10px',
          maxWidth: 220,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={dim.tooltip ?? 'Edit filter'}
      >
        {display}
      </button>
      <button
        type="button"
        onClick={onClear}
        style={{
          ...innerBtn,
          padding: '0 8px 0 4px',
          fontSize: 12,
          opacity: 0.65,
        }}
        title="Remove filter"
        aria-label={`Remove ${dim.label} filter`}
      >
        ✕
      </button>
    </span>
  );
}

// ============================================
// Add-filter menu (popover content)
// ============================================

interface AddFilterMenuProps {
  dimensions: FilterDim[];
  onPick: (key: FilterDimKey) => void;
}

function AddFilterMenu({ dimensions, onPick }: AddFilterMenuProps) {
  const inactive = dimensions.filter((d) => d.selected.length === 0);
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          marginBottom: 6,
          opacity: 0.7,
        }}
      >
        Add filter
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {inactive.length === 0 && (
          <div style={{ fontSize: 11, opacity: 0.5, padding: '6px 2px' }}>
            All filters are already active. Edit the chips below to change values.
          </div>
        )}
        {inactive.map((d) => (
          <button
            key={d.key}
            type="button"
            onClick={() => onPick(d.key)}
            title={d.tooltip}
            style={{
              textAlign: 'left',
              background: 'none',
              border: 'none',
              padding: '5px 4px',
              fontSize: 12,
              color: 'inherit',
              cursor: 'pointer',
              borderRadius: 4,
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'rgba(127,127,127,0.12)')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'none')}
          >
            {d.kind === 'min' ? `${d.label} ≥` : d.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================
// Severity popover
// ============================================

interface SeverityPopoverProps {
  fields: SeverityField[];
  filters: Record<string, number>;
  onChange: (next: Record<string, number>) => void;
  /** Title shown at the top-left of the popover. Default 'Severity'. */
  title?: string;
  /** Filter-param key for the "highest-of-any-category" shortcut. Default
   * `minSeverityHighest`. NatSec uses `minNatSecOverall` (the existing
   * Overall NatSec Score field on each record). */
  highestKey?: string;
  /** Label rendered before the highest selector. Default 'Any category ≥'. */
  highestLabel?: string;
  /** Which scale's level names and definition link to show. Default harm severity. */
  variant?: ImpactPillVariant;
}

function SeverityPopover({
  fields,
  filters,
  onChange,
  title = 'Severity',
  highestKey = SEVERITY_HIGHEST_KEY,
  highestLabel = 'Any category ≥',
  variant = 'standard',
}: SeverityPopoverProps) {
  const scaleLink = SCALE_LINKS[variant];
  const highest = filters[highestKey] || 0;
  const hasPerCat = fields.some((f) => (filters[paramKeyFor(f)] || 0) > 0);

  const setKey = (key: string, val: number) => {
    onChange({ ...filters, [key]: val });
  };

  const clearAll = () => onChange({});

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 11 }}>
          <span style={{ fontWeight: 600, opacity: 0.7 }}>{title}</span>
          <span style={{ marginLeft: 8 }}>
            Based on{' '}
            <a href={scaleLink.href} target="_blank" rel="noreferrer" style={{ color: TEXT_LINK_COLOR }}>
              {scaleLink.text}
            </a>
          </span>
        </span>
        {(highest > 0 || hasPerCat) && (
          <button
            type="button"
            onClick={clearAll}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 10,
              color: 'var(--gc-link, inherit)',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            Clear
          </button>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 11 }}>{highestLabel}</span>
        <SeverityLevelSelect
          variant={variant}
          value={highest}
          onChange={(v) => setKey(highestKey, v)}
        />
      </div>

      <div
        style={{
          fontSize: 10,
          opacity: 0.6,
          textAlign: 'center',
          margin: '6px 0',
        }}
      >
        — or set per category —
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {fields.map((sf) => {
          const key = paramKeyFor(sf);
          return (
            <div
              key={sf.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                opacity: highest > 0 ? 0.45 : 1,
              }}
            >
              <span style={{ fontSize: 11 }}>{sf.label} ≥</span>
              <SeverityLevelSelect
                variant={variant}
                value={filters[key] || 0}
                onChange={(v) => setKey(key, v)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SeverityLevelSelect({ variant, value, onChange }: {
  variant: ImpactPillVariant;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(parseInt((e.target as HTMLSelectElement).value, 10))}
      style={{
        padding: '2px 6px',
        fontSize: 11,
        lineHeight: '14px',
        border: value > 0
          ? '1px solid var(--gc-link, currentColor)'
          : '1px solid var(--gc-control-border, currentColor)',
        borderRadius: 9,
        background: 'transparent',
        color: 'inherit',
        cursor: 'pointer',
      }}
    >
      <option value={0}>Any</option>
      {[1, 2, 3, 4, 5].map((n) => (
        <option key={n} value={n}>{levelName(variant, n)}</option>
      ))}
    </select>
  );
}

// ============================================
// Main filter bar
// ============================================

type OpenPopover =
  | null
  | { kind: 'chip'; dim: FilterDimKey }
  | { kind: 'add' }
  | { kind: 'severity' }
  | { kind: 'natsec' }
  | { kind: 'stackBy' };

export function IncidentFilterBar<S extends string = string>({
  availableStackBy,
  stackBy,
  onStackByChange,
  stackByLabels,
  stackByTooltips,
  stackByControlLabel = 'Stack by',
  allowNoneStackBy = true,
  dimensions,
  severityFields,
  severityFilters,
  onSeverityChange,
  natSecSeverityFields,
  natSecSeverityFilters,
  onNatSecSeverityChange,
  severityLabel = 'Severity',
  natSecSeverityLabel = 'NatSec Severity',
  natSecHighestKey = 'minNatSecOverall',
  natSecHighestLabel = 'Overall ≥',
  hasActiveFilter,
  onResetAll,
  isMobile,
}: IncidentFilterBarProps<S>) {
  const [open, setOpen] = useState<OpenPopover>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const severityBtnRef = useRef<HTMLButtonElement>(null);
  const natSecBtnRef = useRef<HTMLButtonElement>(null);
  const stackByBtnRef = useRef<HTMLButtonElement>(null);
  // One ref per dimension for chip popover anchoring. Stored in a Map keyed by
  // the dimension key so chips can be added/removed without reshuffling refs.
  const chipRefs = useRef(new Map<FilterDimKey, HTMLButtonElement | null>());
  const chipRefObj = (key: FilterDimKey) => ({
    get current() { return chipRefs.current.get(key) ?? null; },
    set current(v: HTMLButtonElement | null) { chipRefs.current.set(key, v); },
  });

  const activeDims = dimensions.filter((d) => d.selected.length > 0);
  const severityActive = Object.values(severityFilters).some((v) => v > 0);
  const natSecActive =
    !!natSecSeverityFilters && Object.values(natSecSeverityFilters).some((v) => v > 0);

  const close = () => setOpen(null);

  return (
    <div style={{ marginBottom: 10 }}>
      {/* All filter controls flow on a single wrapping row: Stack-by selector,
          Add-filter, Severity, active chips, and Clear all. */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 8,
          rowGap: 6,
        }}
      >
        <StackByButton
          available={availableStackBy}
          value={stackBy}
          labels={stackByLabels}
          controlLabel={stackByControlLabel}
          isOpen={open?.kind === 'stackBy'}
          onToggle={() => setOpen(open?.kind === 'stackBy' ? null : { kind: 'stackBy' })}
          triggerRef={stackByBtnRef}
        />

        <button
          ref={addBtnRef}
          type="button"
          onClick={() => setOpen(open?.kind === 'add' ? null : { kind: 'add' })}
          style={{ ...pillStyle('dashed'), opacity: 0.85 }}
        >
          + Add filter
        </button>

        {severityFields.length > 0 && (
          <button
            ref={severityBtnRef}
            type="button"
            onClick={() => setOpen(open?.kind === 'severity' ? null : { kind: 'severity' })}
            style={pillStyle('solid', severityActive)}
          >
            {severitySummary(severityFilters, severityFields, severityLabel)} ▾
          </button>
        )}

        {natSecSeverityFields && natSecSeverityFields.length > 0 && (
          <button
            ref={natSecBtnRef}
            type="button"
            onClick={() => setOpen(open?.kind === 'natsec' ? null : { kind: 'natsec' })}
            style={pillStyle('solid', natSecActive)}
          >
            {severitySummary(
              natSecSeverityFilters ?? {},
              natSecSeverityFields,
              natSecSeverityLabel,
              natSecHighestKey,
              'natsec',
            )} ▾
          </button>
        )}

        {activeDims.map((dim) => (
          <FilterChip
            key={dim.key}
            dim={dim}
            isOpen={open?.kind === 'chip' && open.dim === dim.key}
            onEdit={() => setOpen(
              open?.kind === 'chip' && open.dim === dim.key
                ? null
                : { kind: 'chip', dim: dim.key }
            )}
            onClear={() => dim.onChange([])}
            triggerRef={chipRefObj(dim.key)}
          />
        ))}

        {hasActiveFilter && (
          <button
            type="button"
            onClick={onResetAll}
            style={{
              background: 'none',
              border: 'none',
              padding: '0 4px',
              margin: 0,
              fontSize: 11,
              lineHeight: `${PILL_HEIGHT}px`,
              fontFamily: 'inherit',
              color: 'inherit',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            title="Clear all filters, stacking, and view mode"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Popovers */}
      {open?.kind === 'stackBy' && (
        <Popover anchorRef={stackByBtnRef} onClose={close} isMobile={isMobile} minWidth={180}>
          <StackByMenu
            available={availableStackBy}
            value={stackBy}
            labels={stackByLabels}
            tooltips={stackByTooltips}
            allowNone={allowNoneStackBy}
            controlLabel={stackByControlLabel}
            onPick={(next) => {
              // Menu only emits values from `availableStackBy` (which is S[])
              // or null, so the runtime type is always S | null.
              onStackByChange(next as S | null);
              close();
            }}
          />
        </Popover>
      )}

      {open?.kind === 'add' && (
        <Popover anchorRef={addBtnRef} onClose={close} isMobile={isMobile} minWidth={200}>
          <AddFilterMenu
            dimensions={dimensions}
            onPick={(key) => setOpen({ kind: 'chip', dim: key })}
          />
        </Popover>
      )}

      {open?.kind === 'severity' && (
        <Popover anchorRef={severityBtnRef} onClose={close} isMobile={isMobile} minWidth={260}>
          <SeverityPopover
            fields={severityFields}
            filters={severityFilters}
            onChange={onSeverityChange}
            title={severityLabel}
          />
        </Popover>
      )}

      {open?.kind === 'natsec' && natSecSeverityFields && onNatSecSeverityChange && (
        <Popover anchorRef={natSecBtnRef} onClose={close} isMobile={isMobile} minWidth={280}>
          <SeverityPopover
            fields={natSecSeverityFields}
            filters={natSecSeverityFilters ?? {}}
            onChange={onNatSecSeverityChange}
            title={natSecSeverityLabel}
            highestKey={natSecHighestKey}
            highestLabel={natSecHighestLabel}
            variant="natsec"
          />
        </Popover>
      )}

      {open?.kind === 'chip' && (() => {
        const dim = dimensions.find((d) => d.key === open.dim);
        if (!dim) return null;
        // Chip popover anchors to the chip when one exists (dimension has a
        // selection). When opening for a fresh dimension picked from the Add
        // menu there's no chip yet, so we fall back to the Add button.
        const hasChip = dim.selected.length > 0;
        const anchor = hasChip ? chipRefObj(dim.key) : { current: addBtnRef.current };
        return (
          <Popover anchorRef={anchor} onClose={close} isMobile={isMobile} minWidth={220}>
            <ValuePicker dim={dim} />
          </Popover>
        );
      })()}
    </div>
  );
}
